import { type XYPosition } from '@xyflow/react';

import { isDefined } from 'common';

import { type ReactFlowGroupNode, type ReactFlowNode, isGroupNode } from '../../types';
import { FlowNodeType } from '../types';

/**
 * https://reactflow.dev/learn/layouting/sub-flows
 * > It’s important that your parent nodes appear before their children in the nodes/ defaultNodes array to get processed correctly.
 * This ensures that group nodes are behind children nodes. React flow uses this order to render nodes in the right order, along
 * with dynamically updating z-index based on selection.
 */
export function compareReactFlowNodes(a: ReactFlowNode, b: ReactFlowNode): number {
  // If both are group nodes or neither is, we can compare by type
  if (a.type === b.type) {
    return 0;
  }
  return a.type === FlowNodeType.GroupNode && b.type !== FlowNodeType.GroupNode ? -1 : 1;
}

/**
 * Returns the group nodes whose bounds contain the given flow position, in `nodes` array order.
 * Group nodes are top-level, so their `position` is already in absolute flow coordinates.
 * When the position falls within multiple overlapping groups, the result is ordered so the last
 * entry is the topmost, since React Flow renders later nodes on top.
 */
export function getGroupNodesAtFlowPosition(props: {
  nodes: ReactFlowNode[];
  flowPosition: XYPosition;
}): ReactFlowGroupNode[] {
  const { nodes, flowPosition } = props;
  const groupNodesContainingPosition = nodes.filter(isGroupNode).filter((groupNode) => {
    const width = groupNode.width ?? groupNode.measured?.width;
    const height = groupNode.height ?? groupNode.measured?.height;
    if (!isDefined(width) || !isDefined(height)) {
      return false;
    }
    return (
      flowPosition.x >= groupNode.position.x &&
      flowPosition.x <= groupNode.position.x + width &&
      flowPosition.y >= groupNode.position.y &&
      flowPosition.y <= groupNode.position.y + height
    );
  });
  return groupNodesContainingPosition;
}
